import React from 'react';
import { PhoneForwarded, CalendarDays, Clock, BarChart3, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { AI_BOOKINGS } from '../../data/calendarEvents';
interface TodaysOverviewProps {
  hasData: boolean;
  onNavigate?: (screen: string) => void;
  minutesTotal?: number;
}
export function TodaysOverview({
  hasData,
  onNavigate,
  minutesTotal = 180
}: TodaysOverviewProps) {
  const {
    isLight
  } = useTheme();
  const minutesUsed = Math.min(34, minutesTotal);
  return <div className="space-y-3" data-id="element-666" data-mp="30z17nit01thh">
      <h3 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-667" data-mp="30w57roxlcuqe">
        Today's Overview
      </h3>
      {hasData ? <div className="space-y-3" data-id="element-668" data-mp="3aqdyq86z4fmg">
          {/* Calls & Meetings row */}
          <div className="grid grid-cols-2 gap-3" data-id="element-669" data-mp="2d8dxsrziwtsa">
            <button onClick={() => onNavigate?.('calls')} className={`flex items-center gap-3 p-3.5 rounded-2xl transition-colors text-left ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-670" data-mp="2i7qj38aqxssr">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'text-blue-500 bg-blue-50' : 'text-blue-400 bg-blue-500/10'}`} data-id="element-671" data-mp="3f0zh3owio6ps">
                <PhoneForwarded className="w-5 h-5" data-id="element-672" data-mp="354o3mvl6xatr" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-673" data-mp="304ryuu4o5fd5">
                <span className={`text-xl font-bold block leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-674" data-mp="302yszh1vqz67">
                  12
                </span>
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-675" data-mp="2r5eq63odcuth">
                  Calls Handled
                </span>
              </div>
              <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-676" data-mp="pae0p4yxpauu" />
            </button>

            <button onClick={() => onNavigate?.('bookings')} className={`flex items-center gap-3 p-3.5 rounded-2xl transition-colors text-left ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-677" data-mp="1d8lqh7u24duw">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'text-purple-500 bg-purple-50' : 'text-purple-400 bg-purple-500/10'}`} data-id="element-678" data-mp="1s5uq0otiknxp">
                <CalendarDays className="w-5 h-5" data-id="element-679" data-mp="1i99fjnyq6kmh" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-680" data-mp="1n4f1unw1i1b3">
                <span className={`text-xl font-bold block leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-681" data-mp="6rzwv3nf7wm8">
                  {AI_BOOKINGS.length}
                </span>
                <span className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-682" data-mp="46itf8ce9kkd">
                  Meetings Booked
                </span>
              </div>
              <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-683" data-mp="d6p3ek8vw2es" />
            </button>
          </div>

          {/* Minutes Used — full width with progress bar */}
          <button onClick={() => onNavigate?.('usage')} className={`w-full p-4 rounded-2xl transition-colors text-left ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-684" data-mp="3cn3tu0qyn5bv">
            <div className="flex items-center justify-between mb-2.5" data-id="element-685" data-mp="3llyxp66j83c0">
              <div className="flex items-center gap-2.5" data-id="element-686" data-mp="2xwmq2aqus3bt">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'text-amber-500 bg-amber-50' : 'text-amber-400 bg-amber-500/10'}`} data-id="element-687" data-mp="2sznxhwmu84fq">
                  <Clock className="w-4.5 h-4.5" data-id="element-688" data-mp="3blzmbx17e0h7" />
                </div>
                <div data-id="element-689" data-mp="3gkawk8veuczy">
                  <span className={`text-sm font-bold block leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-690" data-mp="3gizuqelizfbh">
                    Minutes Used
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-691" data-mp="3ubfp6r0d250s">
                <span className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-692" data-mp="3u9uu90a4wk2e">
                  {minutesUsed}
                </span>
                <span className={`text-xs ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-693" data-mp="82wmo7qi3omm">
                  / {minutesTotal} min
                </span>
                <ChevronRight className={`w-3.5 h-3.5 ml-1 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-694" data-mp="29wm4uwna7ipe" />
              </div>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-white/10'}`} data-id="element-695" data-mp="m97z5sayuuxv">
              <div className="h-full rounded-full bg-amber-500 transition-all duration-500" style={{
            width: `${minutesUsed / minutesTotal * 100}%`
          }} data-id="element-696" data-mp="i8o2sqy7vvcs" />
            </div>
          </button>
        </div> : <div className={`rounded-2xl p-6 flex flex-col items-center text-center ${isLight ? 'bg-white/70 border border-slate-100' : 'bg-white/[0.03] border border-white/5'}`} data-id="element-697" data-mp="15z1v8ikprw7w">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-698" data-mp="vyrkr5q2d2wf">
            <BarChart3 className={`w-6 h-6 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-699" data-mp="3gq1qmydvhpi8" />
          </div>
          <p className={`text-sm font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-700" data-mp="3i88v5mve4b25">
            No activity yet
          </p>
          <p className={`text-xs leading-relaxed max-w-[220px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-701" data-mp="396j0m867ow05">
            Once Vexu.ai starts handling calls, your stats will appear here.
          </p>
        </div>}
    </div>;
}